// Pure scale helpers for the horizontal stacked bar chart, kept free of d3 /
// React / CSS imports so the normalised-vs-magnitude math is unit-testable
// without rendering the chart.

const sumSegments = (segments: Record<string, number>) =>
  Object.values(segments).reduce((sum, value) => sum + value, 0);

/**
 * The stacked value for one segment. Normalised charts stack each bar's
 * segments as fractions of that bar, so every bar fills the axis (composition
 * view). Magnitude charts (`normalized: false`) stack the raw values, so bar
 * length encodes the absolute total — useful for ranked "top contributors"
 * breakdowns where the point is comparing magnitudes, not just proportions.
 */
export const stackedSegmentValue = (
  segments: Record<string, number>,
  key: string,
  normalized: boolean
): number => {
  // @ts-ignore noUncheckedIndexedAccess
  const value: number = segments[key] ?? 0;
  if (!normalized) return value;
  const total = sumSegments(segments);
  return total ? value / total : 0;
};

/**
 * The segment's share of its own bar (0–1), used for the tooltip percentage so
 * it reads correctly in magnitude mode too.
 */
export const stackedSegmentShare = (
  segments: Record<string, number>,
  key: string
): number => {
  const total = sumSegments(segments);
  // @ts-ignore noUncheckedIndexedAccess
  const value: number = segments[key] ?? 0;
  return total ? value / total : 0;
};

export type SegmentSpanLayout = { start: number; span: number };

/**
 * Lays out one bar's segment spans (px): non-empty segments get at least
 * `minVisible`px of paint plus the `spacing` gap (no double gap for tiny
 * segments), larger segments shrink proportionally so the bar total stays
 * exact. Falls back to the raw spans if the bar is too short to fit that.
 */
export const adjustSegmentSpans = (
  rawSpans: number[],
  spacing: number,
  minVisible = 1
): SegmentSpanLayout[] => {
  const total = rawSpans.reduce((sum, span) => sum + span, 0);
  const minSpan = spacing + minVisible;
  const nonZeroCount = rawSpans.filter((span) => span > 0).length;

  let spans = rawSpans;
  if (total > 0 && nonZeroCount * minSpan <= total) {
    // Pin undersized spans at the minimum and rescale the rest; rescaling
    // can push more under the minimum, so iterate (each pass pins >= 1).
    const pinned = new Set<number>();
    for (;;) {
      const freeTotal = total - pinned.size * minSpan;
      const freeRawSum = rawSpans.reduce(
        (sum, span, index) =>
          span > 0 && !pinned.has(index) ? sum + span : sum,
        0
      );
      const scale = freeRawSum > 0 ? freeTotal / freeRawSum : 0;
      const newlyPinned = rawSpans.flatMap((span, index) =>
        span > 0 && !pinned.has(index) && span * scale < minSpan ? [index] : []
      );
      if (newlyPinned.length === 0) {
        spans = rawSpans.map((span, index) => {
          if (span === 0) return 0;
          return pinned.has(index) ? minSpan : span * scale;
        });
        break;
      }
      for (const index of newlyPinned) pinned.add(index);
    }
  }

  let cursor = 0;
  return spans.map((span) => {
    const start = cursor;
    cursor += span;
    return { start, span };
  });
};

/**
 * The segment index whose span contains `coord`. Up to `slop` past either
 * end of the bar the nearest end segment still matches; further out is dead.
 */
export const findSegmentSpanAtCoord = (
  layouts: SegmentSpanLayout[],
  coord: number,
  slop: number = Number.POSITIVE_INFINITY
): number | null => {
  let firstIndex: number | null = null;
  let firstStart = 0;
  let lastIndex: number | null = null;
  let lastEnd = 0;
  for (let index = 0; index < layouts.length; index++) {
    const layout = layouts[index];
    if (layout === undefined || layout.span <= 0) continue;
    if (firstIndex === null) {
      firstIndex = index;
      firstStart = layout.start;
    }
    lastIndex = index;
    lastEnd = layout.start + layout.span;
    if (coord >= layout.start && coord <= lastEnd) return index;
  }
  if (coord < firstStart && coord >= firstStart - slop) return firstIndex;
  if (coord > lastEnd && coord <= lastEnd + slop) return lastIndex;
  return null;
};
